import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, unref as _unref, createTextVNode as _createTextVNode, withCtx as _withCtx, toDisplayString as _toDisplayString, Fragment as _Fragment, createElementBlock as _createElementBlock, normalizeClass as _normalizeClass, createVNode as _createVNode } from "vue"

const _hoisted_1 = { key: 0 }

import type { AlternativeFlightsData, MergedOfferItemData } from '@/interfaces/offer';
import FlightContainer from './FlightContainer.vue';
import FlightSegment from './FlightSegment.vue';
import type { SearchFormDataType } from '@/interfaces/search-form';
import { computed, type Ref, ref, useTemplateRef } from 'vue';
import UnverifiedFlightDetails from './UnverifiedFlightDetails.vue';
import RoundedButton from '@lmt-rpb/RoundedButton/RoundedButton.vue';
import { getParam } from '@/components/common/services/url';
import { verifyOffer } from '@/components/common/services/verifyOfferService';
import Loading from '@lmt-rpb/Loading/Loading.vue';
import PriceRow from './PriceRow.vue';
import OfferDetailsModal from '@lmt-rpb/OfferDetailsModal/OfferDetailsModal.vue';
import CheckoutFlightDetails from '@lmt-rpb/CheckoutFlightDetails/CheckoutFlightDetails.vue';

import { getPricing } from '@/js/utils/priceUtils';
import { useOfferStatus } from '@helper/composables/offerStatus';
import { useTranslations } from '@helper/composables/translations';
import type { ClientCurrency } from '@global-js/constants';
import { getBookingUrl } from '@/js/utils/urlUtils';
import SelectContentAnalytics from '@modules/analytics/SelectContentAnalytics';

type Props = {
	alternativeFlight: AlternativeFlightsData;
	price: { amount: number | null; currency: ClientCurrency };
	formData: SearchFormDataType;
};

const REQUEST_CANCEL_TOKEN = 'itemDetails';

export default /*@__PURE__*/_defineComponent({
  __name: 'AlternativeFlight',
  props: {
    alternativeFlight: {},
    price: {},
    formData: {}
  },
  setup(__props: any) {



const verified = ref(false);
const notAvailable = ref(false);
const loading = ref(false);
const isDebuggingAirportsMissing = () => getParam('debuggingMissingAirports') === '1';
const verifiedOffer: Ref<null | MergedOfferItemData> = ref(null);
const status = computed(() => verifiedOffer.value?.Status);
const { translate } = useTranslations();

const { isOfferAvailable, isOfferOnRequest, offerCanBeChecked } = useOfferStatus(status);

const containerClasses = computed(() => ({
	'alternative-flight__flight-container--loading': loading.value,
	...(verified.value && {
		'alternative-flight__flight-container--available': isOfferAvailable.value,
		'alternative-flight__flight-container--on-request': isOfferOnRequest.value,
		'alternative-flight__flight-container--check-unable': offerCanBeChecked.value,
		'alternative-flight__flight-container--not-available': notAvailable.value,
	}),
}));
const outboundDuration = computed(() => {
	if (verifiedOffer.value?.OutboundFlight || __props.alternativeFlight.OutboundFlight) {
		return verifiedOffer.value?.OutboundFlight?.Duration || __props.alternativeFlight.OutboundFlight.Duration;
	}
	return 0;
});

const inboundDuration = computed(() => {
	if (verifiedOffer.value?.InboundFlight || __props.alternativeFlight.InboundFlight) {
		return verifiedOffer.value?.InboundFlight?.Duration || __props.alternativeFlight.InboundFlight.Duration;
	}
	return 0;
});

const offerPrice = computed(() => getPricing(__props.alternativeFlight.Pricing));
const verifiedPrice = computed(() => {
	if (verifiedOffer.value === undefined || verifiedOffer.value === null) {
		return null;
	}
	if (verifiedOffer.value.Status === 'CheckUnable' || verifiedOffer.value.Status === 'Undefined') {
		return offerPrice.value.amount;
	}
	return typeof verifiedOffer.value.Price === 'string' ? Number.parseFloat(verifiedOffer.value.Price) : verifiedOffer.value.Price;
});

const priceDifference = computed(() => {
	const { amount: currentPrice, currency } = __props.price;
	if (verifiedPrice.value === null || currentPrice === null) {
		return {
			difference: 0,
			currency,
		};
	}
	return {
		difference: verifiedPrice.value - currentPrice,
		currency,
	};
});

const hasDifference = computed(() => {
	return verified.value && !notAvailable.value && priceDifference.value.difference !== 0;
});
const priceText = computed(() => (priceDifference.value.difference > 0 ? translate('pricing.surcharge') : translate('pricing.discount')));
const priceColor = computed(() => (priceDifference.value.difference > 0 ? 'accent' : 'primary'));
const pricePrefix = computed(() => (priceDifference.value.difference > 0 ? '+' : '-'));

const bookingUrl = computed(() => getBookingUrl(verifiedOffer.value, __props.alternativeFlight));

const flightDetailsModal = useTemplateRef('flightDetailsModal');
const showModal = (): void => {
	flightDetailsModal.value?.open();
};

const fireAnalytics = () => {
	SelectContentAnalytics.selectContent({
		cId: 'offerlist_alternativeflights_verifiedoffer_button',
	});
};

const verifyAlternativeFlight = async (): Promise<MergedOfferItemData | null> => {
	SelectContentAnalytics.selectContent({
		cId: 'offerlist_alternativeflights_unverifiedoffer_button',
	});

	loading.value = true;
	const itemData = {
		type: 'package',
		available: null,
		loading: false,
		duration: 0,
		Price: __props.alternativeFlight.Price && __props.alternativeFlight.Price.Amount ? __props.alternativeFlight.Price.Amount : 0,
		CurrencyCode: __props.alternativeFlight.Price.Currency,
		StartDate: __props.alternativeFlight.OutboundFlight ? __props.alternativeFlight.OutboundFlight.Departure.Date : '',
		EndDate: __props.alternativeFlight.InboundFlight.Departure.Date,
		OfferID: __props.alternativeFlight.OfferID,
	} as MergedOfferItemData;

	const cancelTokenName = `${REQUEST_CANCEL_TOKEN}-${itemData.OfferID}`;
	try {
		const results = (await verifyOffer(isDebuggingAirportsMissing(), __props.formData, itemData, cancelTokenName)) as MergedOfferItemData;
		loading.value = false;

		if (!results?.available) {
			verified.value = true;
			notAvailable.value = true;
			return results;
		}
		if (results?.available) {
			verifiedOffer.value = results;
			verified.value = true;
			return results;
		}
	} catch (error) {
		console.error('vacancyCheck error', error);
	} finally {
		loading.value = false;
	}
	return null;
};

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(['alternative-flight', { 'alternative-flight--loading': loading.value }])
  }, [
    (loading.value)
      ? (_openBlock(), _createBlock(Loading, {
          key: 0,
          size: "medium",
          class: "alternative-flight__loader"
        }))
      : _createCommentVNode("v-if", true),
    _createVNode(FlightContainer, {
      class: _normalizeClass(['alternative-flight__flight-container', containerClasses.value]),
      "vertical-divider": false,
      "with-flight-details": verified.value && !notAvailable.value,
      onShowFlightDetails: showModal
    }, {
      outbound: _withCtx(() => [
        (verified.value && verifiedOffer.value)
          ? (_openBlock(), _createBlock(FlightSegment, {
              key: 0,
              "flight-segments": verifiedOffer.value.OutboundFlightSegments,
              duration: outboundDuration.value
            }, null, 8 /* PROPS */, ["flight-segments", "duration"]))
          : (_openBlock(), _createBlock(UnverifiedFlightDetails, {
              key: 1,
              "flight-data": _ctx.alternativeFlight.OutboundFlight,
              duration: outboundDuration.value
            }, null, 8 /* PROPS */, ["flight-data", "duration"]))
      ]),
      inbound: _withCtx(() => [
        (verified.value && verifiedOffer.value)
          ? (_openBlock(), _createBlock(FlightSegment, {
              key: 0,
              "flight-segments": verifiedOffer.value.InboundFlightSegments,
              duration: inboundDuration.value
            }, null, 8 /* PROPS */, ["flight-segments", "duration"]))
          : (_openBlock(), _createBlock(UnverifiedFlightDetails, {
              key: 1,
              "flight-data": _ctx.alternativeFlight.InboundFlight,
              duration: inboundDuration.value
            }, null, 8 /* PROPS */, ["flight-data", "duration"]))
      ]),
      price: _withCtx(() => [
        (hasDifference.value)
          ? (_openBlock(), _createBlock(PriceRow, {
              key: 0,
              "price-props": {
						price: Math.abs(priceDifference.value.difference),
						currency: priceDifference.value.currency,
						showDecimals: true,
						prefix: pricePrefix.value,
						theme: priceColor.value,
					},
              class: "alternative-flight__extra-price",
              "font-size": "small",
              text: priceText.value
            }, null, 8 /* PROPS */, ["price-props", "text"]))
          : _createCommentVNode("v-if", true),
        (!notAvailable.value && offerPrice.value.amount)
          ? (_openBlock(), _createBlock(PriceRow, {
              key: 1,
              class: "alternative-flight__price",
              "price-props": {
						price: verifiedPrice.value ?? offerPrice.value.amount,
						currency: offerPrice.value.currency,
						showDecimals: !!verifiedPrice.value,
						theme: 'inherit',
					},
              "font-size": hasDifference.value ? 'medium' : 'large',
              text: _unref(translate)('pricing.total')
            }, null, 8 /* PROPS */, ["price-props", "font-size", "text"]))
          : _createCommentVNode("v-if", true)
      ]),
      button: _withCtx(() => [
        (verified.value)
          ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
              (notAvailable.value)
                ? (_openBlock(), _createBlock(RoundedButton, {
                    key: 0,
                    class: "alternative-flight__button",
                    disabled: true
                  }, {
                    default: _withCtx(() => _cache[0] || (_cache[0] = [
                      _createTextVNode(" Nicht mehr verfügbar ")
                    ])),
                    _: 1 /* STABLE */
                  }))
                : (_openBlock(), _createBlock(RoundedButton, {
                    key: 1,
                    class: "alternative-flight__button",
                    theme: "extra",
                    as: "a",
                    href: bookingUrl.value,
                    rel: "nofollow",
                    onClick: fireAnalytics
                  }, {
                    default: _withCtx(() => [
                      _createTextVNode(_toDisplayString(_unref(translate)('offer.addToCart')), 1 /* TEXT */)
                    ]),
                    _: 1 /* STABLE */
                  }, 8 /* PROPS */, ["href"]))
            ], 64 /* STABLE_FRAGMENT */))
          : (_openBlock(), _createBlock(RoundedButton, {
              key: 1,
              class: "alternative-flight__button",
              disabled: loading.value,
              onClick: verifyAlternativeFlight
            }, {
              default: _withCtx(() => [
                _createTextVNode(_toDisplayString(_unref(translate)('offer.verifyOffer')), 1 /* TEXT */)
              ]),
              _: 1 /* STABLE */
            }, 8 /* PROPS */, ["disabled"]))
      ]),
      _: 1 /* STABLE */
    }, 8 /* PROPS */, ["class", "with-flight-details"]),
    _createVNode(OfferDetailsModal, {
      ref_key: "flightDetailsModal",
      ref: flightDetailsModal,
      class: "alternative-flight__flight-details-modal",
      title: _unref(translate)('offer.flight.details')
    }, {
      default: _withCtx(() => [
        (verifiedOffer.value?.InboundFlightSegments && verifiedOffer.value?.OutboundFlightSegments)
          ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
              _createVNode(CheckoutFlightDetails, {
                "inbound-flight": verifiedOffer.value.InboundFlightSegments,
                "outbound-flight": verifiedOffer.value.OutboundFlightSegments,
                "has-stopover": 
						verified.value && !notAvailable.value
							? !!(verifiedOffer.value?.InboundFlightSegments.length > 1 || verifiedOffer.value.OutboundFlightSegments.length > 1)
							: ''
					
              }, null, 8 /* PROPS */, ["inbound-flight", "outbound-flight", "has-stopover"])
            ]))
          : _createCommentVNode("v-if", true)
      ]),
      _: 1 /* STABLE */
    }, 8 /* PROPS */, ["title"])
  ], 2 /* CLASS */))
}
}

})